import { useEffect, useMemo } from "react";
import Select from "react-select";
import { useParams } from "react-router-dom";
import ModalGroupingTable from "../components/ModalGroupingTable";
import CostAllocation from "../components/CostAllocation.js";
import { useLazyGetDropDownsForProductsAndServicesQuery } from "../../../../../../app/services/productAndService/productAndServiceApi";
import {
  useLazyGetCertificateGroupingDetailsForGWPGeneralByIdQuery,
  useLazyGetProductDetailsListByProductInformationIdQuery,
} from "../../../../../../app/services/CertificateGrouping/certificateGroupingApi";
import { decryptAESID } from "../../../../../../utils/Encryption";

const TICK_OPTIONS = [
  { label: "1 Tick", value: 1 },
  { label: "2 Ticks", value: 2 },
  { label: "3 Ticks", value: 3 },
  { label: "4 Ticks", value: 4 },
];

const toOptions = (items = [], dropdownName) =>
  items
    .filter((item) => item.dropdownName === dropdownName)
    .map((item) => ({ value: item.id, label: item.name }));

const findOption = (options, value) =>
  options.find((option) => option.value === value) ?? null;

const splitCertificateNumber = (certificateNumber) => {
  const value = String(certificateNumber ?? "");
  const slashIndex = value.lastIndexOf("/");
  if (slashIndex === -1) return { base: value, suffix: 0 };
  return {
    base: value.slice(0, slashIndex),
    suffix: Number(value.slice(slashIndex + 1)) || 0,
  };
};

const buildCostAllocationRows = (products, allocations, itemCodeOptions) => {
  const labels = [
    ...new Set(
      products.map(({ applicationNumber, certPrefix }) =>
        certPrefix ? `${applicationNumber}/${certPrefix}` : applicationNumber
      )
    ),
  ];

  return labels.map((label) => {
    const { base, suffix } = splitCertificateNumber(label);
    const allocation = allocations.find((item) => {
      const current = splitCertificateNumber(item.certificateNumber);
      return current.base === base && current.suffix === suffix;
    });

    return {
      label,
      value: allocation ? findOption(itemCodeOptions, allocation.itemCode) : null,
      tickValue: allocation ? findOption(TICK_OPTIONS, allocation.tickRating) : null,
      declared: allocation?.declaredGWPValue ?? null,
    };
  });
};

const ReadOnlyTextarea = ({ label, value }) => (
  <div className="mt-2">
    <label className="form-label">{label}</label>
    <textarea className="form-control mt-1" rows="4" value={value} disabled />
  </div>
);

const ReadOnlySelectRow = ({ label, value, options, placeholder, className = "" }) => (
  <div className={`d-flex align-items-center ${className}`}>
    <label className="form-label mb-0 w-20">{label}</label>
    <div style={{ flex: 1 }}>
      <Select
        className="custom-checkbox-select"
        value={value}
        options={options}
        placeholder={placeholder}
        isDisabled
      />
    </div>
  </div>
);

const GWPGeneralCertificate = () => {
  const { id } = useParams();
  const [loadDropdowns, { data: dropdownData }] = useLazyGetDropDownsForProductsAndServicesQuery();
  const [loadProducts, { data: productData }] = useLazyGetProductDetailsListByProductInformationIdQuery();
  const [loadGroupingDetails, { data: groupingData }] = useLazyGetCertificateGroupingDetailsForGWPGeneralByIdQuery();
  const applicationId = useMemo(() => decryptAESID(id, false), [id]);

  useEffect(() => {
    if (!(applicationId > 0)) return;
    loadDropdowns();
    loadProducts(applicationId);
    loadGroupingDetails(applicationId);
  }, [applicationId, loadDropdowns, loadProducts, loadGroupingDetails]);

  const products = useMemo(
    () => (Array.isArray(productData) ? productData : productData?.data ?? []),
    [productData]
  );

  const approverOptions = useMemo(() => toOptions(dropdownData, "Approver List"), [dropdownData]);
  const itemCodeOptions = useMemo(() => toOptions(dropdownData, "Items Codes"), [dropdownData]);

  const costAllocationRows = useMemo(
    () =>
      buildCostAllocationRows(
        products,
        groupingData?.getCertificateAndCostAllocationForGWPGeneralList ?? [],
        itemCodeOptions
      ),
    [products, groupingData, itemCodeOptions]
  );

  const peerReviewer = findOption(approverOptions, groupingData?.peerReviewerId);
  const approver = findOption(approverOptions, groupingData?.approverId);
  const caseOfficerComment = groupingData?.commentFromCaseOfficer ?? "";

  return (
    <div className="certificate-grouping-list-page">
      <div className="crud-requirement-list">
        <div className="corporate-member-list">
          <div className="table-title mt-2">
            <h3>Model Grouping</h3>
          </div>
          <ModalGroupingTable type="GWP_GENERAL" data={products} />

          <div className="table-title mt-4">
            <h3>Certificate and Cost Allocation</h3>
          </div>
          <CostAllocation
            type="GWP_GENERAL"
            rows={costAllocationRows}
            itemCodeOptions={itemCodeOptions}
            tickOptions={TICK_OPTIONS}
          />

          <div className="table-title mt-5 mb-4">
            <h3>Internal Notes</h3>
          </div>
          <ReadOnlyTextarea label="Comments from Case Officer" value={caseOfficerComment} />

          <div className="table-title mt-5 mb-4">
            <h3>Assign to</h3>
          </div>
          <div className="dropdown-select-wrapper">
            <ReadOnlySelectRow
              label="Peer Reviewer"
              value={peerReviewer}
              options={approverOptions}
              placeholder="Select Peer Reviewer"
              className="mb-4"
            />
            <ReadOnlySelectRow
              label="Approver"
              value={approver}
              options={approverOptions}
              placeholder="Select Approver"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GWPGeneralCertificate;